import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { employeeService } from "../../services/employeeService";
import { createTrackingHubConnection } from "../../lib/signalr";
import { toast } from "../../services/toastService";
import Loading from "../../components/Loading";
import "leaflet/dist/leaflet.css";
import "../../utils/leafletIconFix";

const ONLINE_THRESHOLD_MS = 3 * 60 * 1000;

const RecenterOnChange = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom() < 13 ? 15 : map.getZoom());
    }
  }, [position, map]);

  return null;
};

const EmployeeTracking = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [position, setPosition] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionState, setConnectionState] = useState("connecting");
  const [, forceTick] = useState(0);
  const connectionRef = useRef(null);

  useEffect(() => {
    loadEmployee();
    const connection = createTrackingHubConnection();
    connectionRef.current = connection;

    connection.on("ReceiveLocation", (location) => {
      setPosition([location.latitude, location.longitude]);
      setLastUpdatedAt(location.recordedAtUtc);
    });

    connection.onreconnecting(() => setConnectionState("connecting"));
    connection.onreconnected(() => {
      setConnectionState("connected");
      connection.invoke("JoinEmployeeGroup", Number(id)).catch(() => {});
    });
    connection.onclose(() => setConnectionState("disconnected"));

    connection
      .start()
      .then(() => {
        setConnectionState("connected");
        return connection.invoke("JoinEmployeeGroup", Number(id));
      })
      .catch(() => {
        setConnectionState("disconnected");
        toast.error("Unable to connect for live updates. Showing last known location only.");
      });

    // Refresh the "last updated X ago" text every 30s without needing new data.
    const tickInterval = setInterval(() => forceTick((t) => t + 1), 30000);

    return () => {
      clearInterval(tickInterval);
      connection.invoke("LeaveEmployeeGroup", Number(id)).catch(() => {});
      connection.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEmployee = async () => {
    try {
      setLoading(true);
      const res = await employeeService.getById(id);
      if (!res.isSuccess) {
        toast.error(res.errors[0] || "Failed to load employee");
        return;
      }
      setEmployee(res.value);
      if (res.value.lastLatitude != null && res.value.lastLongitude != null) {
        setPosition([res.value.lastLatitude, res.value.lastLongitude]);
        setLastUpdatedAt(res.value.lastLocationAtUtc);
      }
    } catch (error) {
      toast.error("An error occurred while loading the employee");
    } finally {
      setLoading(false);
    }
  };

  const isOnline = lastUpdatedAt
    ? Date.now() - new Date(`${lastUpdatedAt}Z`).getTime() < ONLINE_THRESHOLD_MS
    : false;

  const lastUpdatedText = lastUpdatedAt
    ? new Date(`${lastUpdatedAt}Z`).toLocaleString()
    : "No location received yet";

  if (loading) return <Loading />;

  return (
    <section className="section-documents">
      <div className="my-card">
        <div className="my-card-header">
          <div className="my-card-title">
            <Link to="/employees" className="text-decoration-none me-2">
              <i className="fas fa-arrow-left" />
            </Link>
            Tracking: {employee?.fullName}
          </div>
          <div>
            {isOnline ? (
              <span className="badge bg-success">Online</span>
            ) : (
              <span className="badge bg-secondary">Offline</span>
            )}
            {connectionState !== "connected" && (
              <span className="badge bg-warning text-dark ms-2">
                {connectionState === "connecting" ? "Connecting..." : "Live updates unavailable"}
              </span>
            )}
          </div>
        </div>
        <div className="my-card-body">
          <p className="mb-2">
            <strong>Email:</strong> {employee?.email} &nbsp;|&nbsp;
            <strong> Last update:</strong> {lastUpdatedText}
          </p>

          {position ? (
            <MapContainer
              center={position}
              zoom={15}
              style={{ height: "500px", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={position}>
                <Popup>
                  {employee?.fullName}
                  <br />
                  Last update: {lastUpdatedText}
                </Popup>
              </Marker>
              <RecenterOnChange position={position} />
            </MapContainer>
          ) : (
            <div className="text-center text-muted py-5">
              <i className="fas fa-location-dot fa-2x mb-3 d-block" />
              Waiting for this employee's device to share a location. The map will appear
              automatically once the first GPS update arrives.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EmployeeTracking;
